import { ReactElement, useState } from 'react';
import {
    UIFormInput,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISelectInput,
    UISelectValue,
    UISwitch,
    UIText
} from '@/ui';
import { useBulkUpdateDeviceWifiAction } from '../actions';
import { useDevicesTranslation } from '../hooks';
import { PendingConfigNote } from './pending-config-note';

const WIFI_SLOT_COUNT = 5;

export type BulkWifiModalProps = {
    companyId: string;
    deviceIds: string[];
    isOpen: boolean;
    onClose: () => void;
    onApplied: () => void;
};

export function BulkWifiModal(props: BulkWifiModalProps): ReactElement {
    const { isOpen, onClose } = props;
    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <FormContent {...props} /> : null}
        </UIModal>
    );
}

function FormContent(props: BulkWifiModalProps): ReactElement {
    const { companyId, deviceIds, onClose, onApplied } = props;
    const { t } = useDevicesTranslation();
    const { bulkUpdateDeviceWifi, loading } = useBulkUpdateDeviceWifiAction({
        companyId
    });

    const slotOptions: UISelectValue[] = Array.from(
        { length: WIFI_SLOT_COUNT },
        (_, index) => ({
            value: String(index),
            label: t('shared.devices.wifi_slot_label', { number: index + 1 })
        })
    );

    const [slot, setSlot] = useState<UISelectValue>(slotOptions[0]);
    const [enabled, setEnabled] = useState(true);
    const [ssid, setSsid] = useState('');
    const [password, setPassword] = useState('');
    const [showErrors, setShowErrors] = useState(false);

    function ssidError(): string {
        if (ssid.trim().length === 0) {
            return t('shared.fields.ssid.error_required');
        }
        if (ssid.length > 32) {
            return t('shared.fields.ssid.error_too_long');
        }
        return '';
    }

    function passwordError(): string {
        if (password.length === 0) {
            return t('shared.fields.wifi_password.error_required');
        }
        if (password.length > 32) {
            return t('shared.fields.wifi_password.error_too_long');
        }
        return '';
    }

    function onApply() {
        setShowErrors(true);
        if (enabled && (ssidError() || passwordError())) {
            return;
        }
        bulkUpdateDeviceWifi(
            enabled
                ? {
                      deviceIds,
                      slot: Number(slot.value),
                      enabled: true,
                      ssid: ssid.trim(),
                      password
                  }
                : {
                      deviceIds,
                      slot: Number(slot.value),
                      enabled: false,
                      ssid: null,
                      password: null
                  }
        )
            .then(() => onApplied())
            .catch(() => {}); // Error handled in the action
    }

    return (
        <>
            <UIModalHeader
                title={t('components.bulk_wifi_modal.title')}
                subtitle={
                    deviceIds.length > 1
                        ? t('shared.devices.bulk_applies_to', {
                              count: deviceIds.length
                          })
                        : undefined
                }
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16
                    }}
                >
                    <PendingConfigNote />

                    <UISelectInput
                        label={t(
                            'components.bulk_wifi_modal.slot_select_label'
                        )}
                        value={slot}
                        options={slotOptions}
                        clearable={false}
                        onChange={(option) => {
                            if (option) {
                                setSlot(option);
                            }
                        }}
                    />

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <UIText>
                                {t('shared.devices.wifi_enabled_label')}
                            </UIText>
                        </div>
                        <UISwitch checked={enabled} onChange={setEnabled} />
                    </div>

                    {enabled && (
                        <>
                            <UIFormInput
                                label={t('shared.fields.ssid.label')}
                                description={t(
                                    'shared.fields.ssid.description'
                                )}
                                value={ssid}
                                maxLength={32}
                                onChange={setSsid}
                                errorText={showErrors ? ssidError() : ''}
                            />
                            <UIFormInput
                                label={t('shared.fields.wifi_password.label')}
                                description={t(
                                    'shared.fields.wifi_password.description'
                                )}
                                value={password}
                                maxLength={32}
                                onChange={setPassword}
                                errorText={showErrors ? passwordError() : ''}
                            />
                        </>
                    )}
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'button',
                        text: t('shared.labels.apply'),
                        loading: loading,
                        disabled: loading,
                        onClick: onApply
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </>
    );
}
